// src/pages/Organizations/Marketing/components/AssetGenerator.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Snackbar,
  Alert,
  useTheme,
  Tabs,
  Tab,
  SelectChangeEvent,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { generateImage, StabilityAIStylePreset } from '../../../../services/stabilityAIService-new';
import { useMarketingStudio, MarketingAsset } from '../../../../contexts/MarketingStudioContext';

interface AssetGeneratorProps {
  organizationId: string;
  organizationName: string;
  branding: {
    logo?: string;
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    brandVoice: string;
    targetAudience: string;
    industry: string;
  };
}

type AssetType = 'social' | 'event' | 'promotional' | 'logo';

interface AssetSize {
  name: string;
  width: number;
  height: number;
  platform?: 'facebook' | 'instagram' | 'twitter' | 'linkedin' | 'email' | 'web';
}

// Asset size presets mapped by type
const assetSizes: Record<AssetType, AssetSize[]> = {
  social: [
    { name: 'Instagram Post', width: 1080, height: 1080, platform: 'instagram' },
    { name: 'Facebook Post', width: 1200, height: 630, platform: 'facebook' },
    { name: 'Twitter Post', width: 1200, height: 675, platform: 'twitter' },
    { name: 'LinkedIn Post', width: 1200, height: 627, platform: 'linkedin' },
  ],
  event: [
    { name: 'Event Banner', width: 1920, height: 1080 },
    { name: 'Event Square', width: 1080, height: 1080 },
    { name: 'Event Mobile', width: 1080, height: 1920 },
  ],
  promotional: [
    { name: 'Web Banner', width: 1200, height: 600 },
    { name: 'Email Header', width: 600, height: 200 },
    { name: 'Digital Ad', width: 1200, height: 628 },
  ],
  logo: [
    { name: 'Square Logo', width: 512, height: 512 },
    { name: 'Wide Logo', width: 800, height: 400 },
  ],
};

// Preset prompts to help users
const promptTemplates: Record<AssetType, string[]> = {
  social: [
    'A vibrant social media post showcasing our products with lifestyle imagery',
    'An engaging announcement post for a new product launch',
    'A professional infographic explaining our services',
  ],
  event: [
    'An elegant event banner for our upcoming conference',
    'A vibrant promotional banner for our networking event',
    'A professional banner for a corporate workshop',
  ],
  promotional: [
    'A sleek promotional banner highlighting our special offers',
    'A professional advertisement featuring our product benefits',
    'A modern web banner showcasing our brand values',
  ],
  logo: [
    'A modern, minimal logo design for our brand',
    'A professional corporate logo with abstract elements',
    'A creative, unique logo that represents innovation',
  ],
};

const AssetGenerator: React.FC<AssetGeneratorProps> = ({ organizationId, organizationName, branding }) => {
  const theme = useTheme();
  const { marketingAssets, addMarketingAsset, deleteMarketingAsset } = useMarketingStudio();
  
  // Asset generation state
  const [assetType, setAssetType] = useState<AssetType>('social');
  const [selectedSize, setSelectedSize] = useState<AssetSize>(assetSizes.social[0]);
  const [prompt, setPrompt] = useState('');
  const [stylePreset, setStylePreset] = useState<StabilityAIStylePreset>('digital-art');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  
  // Asset gallery state
  const [assetTabValue, setAssetTabValue] = useState(0);
  const [filteredAssets, setFilteredAssets] = useState<MarketingAsset[]>([]);
  
  // Filter assets when tab changes or when new assets are added
  useEffect(() => {
    let filtered: MarketingAsset[];
    
    if (assetTabValue === 0) {
      // All assets
      filtered = marketingAssets.filter(asset => asset.organizationId === organizationId);
    } else if (assetTabValue === 1) {
      // Social media assets
      filtered = marketingAssets.filter(
        asset => asset.organizationId === organizationId && 
        asset.metadata.platform && 
        ['facebook', 'instagram', 'twitter', 'linkedin'].includes(asset.metadata.platform)
      );
    } else if (assetTabValue === 2) {
      // Event assets
      filtered = marketingAssets.filter(
        asset => asset.organizationId === organizationId && 
        asset.tags.includes('event')
      );
    } else {
      // Promotional assets
      filtered = marketingAssets.filter(
        asset => asset.organizationId === organizationId && 
        asset.tags.includes('promotional')
      );
    }
    
    setFilteredAssets(filtered);
  }, [assetTabValue, marketingAssets, organizationId]);
  
  // Update prompt when template or asset type changes
  useEffect(() => {
    if (promptTemplates[assetType] && promptTemplates[assetType].length > 0) {
      setPrompt(promptTemplates[assetType][0]);
    }
  }, [assetType]);
  
  // Handle asset type change
  const handleAssetTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value as AssetType;
    setAssetType(newType);
    setSelectedSize(assetSizes[newType][0]);
  };
  
  // Handle size change
  const handleSizeChange = (event: SelectChangeEvent) => {
    const sizeName = event.target.value;
    const newSize = assetSizes[assetType].find(size => size.name === sizeName);
    if (newSize) {
      setSelectedSize(newSize);
    }
  };
  
  // Handle style preset change
  const handleStylePresetChange = (event: SelectChangeEvent) => {
    setStylePreset(event.target.value as StabilityAIStylePreset);
  };
  
  // Handle asset gallery tab change
  const handleAssetTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setAssetTabValue(newValue);
  };
  
  // Handle template selection
  const handleTemplateSelect = (template: string) => {
    setPrompt(template);
  };
  
  // Generate asset
  const handleGenerateAsset = async () => {
    if (!prompt) {
      setError('Please enter a prompt');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    setGeneratedImageUrl(null);
    
    try {
      // Generate asset using Stability AI
      const response = await generateImage({
        prompt: `${prompt}, ${selectedSize.platform || ''} image, ${organizationName} brand, ${branding.primaryColor} color scheme`,
        stylePreset,
        negativePrompt: "blurry, bad quality, distorted, disfigured, low resolution"
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      // Get the first image from the response
      if (response.images && response.images.length > 0) {
        // Convert base64 to a data URL
        const imageUrl = `data:image/png;base64,${response.images[0].base64}`;
        setGeneratedImageUrl(imageUrl);
        setSuccess(true);
      } else {
        throw new Error('No images generated');
      }
    } catch (error) {
      console.error('Error generating asset:', error);
      setError('Failed to generate asset. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Save the generated asset
  const handleSaveAsset = () => {
    if (!generatedImageUrl) return;
    
    const newAsset: MarketingAsset = {
      id: uuidv4(),
      name: `${assetType.charAt(0).toUpperCase() + assetType.slice(1)} - ${selectedSize.name}`,
      type: 'image',
      url: generatedImageUrl,
      thumbnailUrl: generatedImageUrl,
      organizationId,
      metadata: {
        prompt,
        aiProvider: 'stability',
        dimensions: {
          width: selectedSize.width,
          height: selectedSize.height
        },
        platform: selectedSize.platform as 'facebook' | 'instagram' | 'twitter' | 'linkedin' | 'email' | 'web' | undefined,
      },
      tags: [assetType],
      createdAt: new Date().toISOString(),
      generatedBy: 'current-user-id', // This would be set to the actual user ID
    };
    
    addMarketingAsset(newAsset);
    setSuccess(true);
    setGeneratedImageUrl(null);
  };
  
  // Delete an asset
  const handleDeleteAsset = (assetId: string) => {
    deleteMarketingAsset(assetId);
  };
  
  // Handle close of success alert
  const handleSuccessClose = () => {
    setSuccess(false);
  };
  
  return (
    <Box>
      <Grid container spacing={3}>
        {/* Asset Generation Form */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" gutterBottom>
              Create Marketing Asset
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="asset-type-label">Asset Type</InputLabel>
                  <Select
                    labelId="asset-type-label"
                    id="asset-type"
                    value={assetType}
                    label="Asset Type"
                    onChange={handleAssetTypeChange}
                  >
                    <MenuItem value="social">Social Media</MenuItem>
                    <MenuItem value="event">Event Graphics</MenuItem>
                    <MenuItem value="promotional">Promotional Material</MenuItem>
                    <MenuItem value="logo">Logo & Branding</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="size-label">Size</InputLabel>
                  <Select
                    labelId="size-label"
                    id="size"
                    value={selectedSize.name}
                    label="Size"
                    onChange={handleSizeChange}
                  >
                    {assetSizes[assetType].map((size) => (
                      <MenuItem key={size.name} value={size.name}>
                        {size.name} ({size.width}x{size.height})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="style-preset-label">Style Preset</InputLabel>
                  <Select
                    labelId="style-preset-label"
                    id="style-preset"
                    value={stylePreset}
                    label="Style Preset"
                    onChange={handleStylePresetChange}
                  >
                    <MenuItem value="photographic">Photographic</MenuItem>
                    <MenuItem value="digital-art">Digital Art</MenuItem>
                    <MenuItem value="cinematic">Cinematic</MenuItem>
                    <MenuItem value="anime">Anime</MenuItem>
                    <MenuItem value="fantasy-art">Fantasy Art</MenuItem>
                    <MenuItem value="3d-model">3D Model</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe what you want to generate..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Prompt Templates
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {promptTemplates[assetType].map((template, index) => (
                    <Chip
                      key={index}
                      label={template.substring(0, 24) + (template.length > 24 ? '...' : '')}
                      onClick={() => handleTemplateSelect(template)}
                      variant="outlined"
                      sx={{ margin: '4px' }}
                    />
                  ))}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateAsset}
                  disabled={isGenerating || !prompt}
                  startIcon={isGenerating ? <CircularProgress size={20} /> : <AddIcon />}
                >
                  {isGenerating ? 'Generating...' : 'Generate Asset'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Preview and Generated Asset */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, height: '100%', border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" gutterBottom>
              {generatedImageUrl ? 'Generated Asset' : 'Preview'}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {isGenerating ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                height: '400px'
              }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Generating with Stability AI...
                </Typography>
              </Box>
            ) : generatedImageUrl ? (
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={generatedImageUrl} 
                  alt="Generated asset" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)'
                  }} 
                />
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleSaveAsset}
                  >
                    Save to Gallery
                  </Button>
                  <Button 
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => window.open(generatedImageUrl, '_blank')}
                  >
                    Download
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                height: '400px',
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: '8px',
                backgroundColor: theme.palette.background.default
              }}>
                <Typography variant="body1" color="text.secondary">
                  Your generated asset will appear here
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedSize.width} × {selectedSize.height} pixels
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Asset Gallery */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mt: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Asset Gallery</Typography>
            </Box>
            
            <Tabs
              value={assetTabValue}
              onChange={handleAssetTabChange}
              textColor="primary"
              indicatorColor="primary"
              aria-label="asset gallery tabs"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <Tab label="All Assets" />
              <Tab label="Social Media" />
              <Tab label="Event Graphics" />
              <Tab label="Promotional" />
            </Tabs>
            
            {filteredAssets.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                height: '200px',
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: '8px',
                backgroundColor: theme.palette.background.default
              }}>
                <Typography variant="body1" color="text.secondary">
                  No assets found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Generate your first asset to get started
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredAssets.map((asset) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={asset.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardMedia
                        component="img"
                        height="160"
                        image={asset.url}
                        alt={asset.name}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="subtitle1" component="div" noWrap>
                          {asset.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {asset.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {asset.metadata.platform && (
                            <Chip
                              label={asset.metadata.platform}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          startIcon={<ShareIcon />}
                        >
                          Share
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteAsset(asset.id)}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Success/Error notifications */}
      <Snackbar open={success} autoHideDuration={6000} onClose={handleSuccessClose}>
        <Alert onClose={handleSuccessClose} severity="success" sx={{ width: '100%' }}>
          Asset generated successfully!
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssetGenerator;
